import React from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { ReactComponent as EyeIcon } from '../../assets/icons/eye.svg';
import { FailedSubscriptionPaymentItem } from '../../api/subscription';
import { formatUKDate } from '../../utils';
import Button from '../BaseComponents/Button';
import './FailedPaymentsTable.scss';

interface FailedPaymentRowProps {
    rowData: FailedSubscriptionPaymentItem;
}

const formatAmount = (amount: number | null | undefined, currency: string): string => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: currency || 'GBP',
        minimumFractionDigits: 2
    }).format(amount);
};

const formatStatus = (status?: string | null): string =>
    status ? status.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()) : '-';

const FailedPaymentRow: React.FC<FailedPaymentRowProps> = ({ rowData }) => {
    const navigate = useNavigate();

    const memberName = `${rowData.member_first_name || ''} ${rowData.member_last_name || ''}`.trim();
    // Stripe subscriptions not linked to a local member fall back to the invoice's customer details.
    const displayName = memberName || rowData.customer_name || 'Unknown member';
    const displayEmail = rowData.member_email || rowData.customer_email || '';

    const handleViewMember = () => {
        if (rowData.member_id) {
            navigate(`/members/${rowData.member_id}`);
        }
    };

    const handleCopyLink = async () => {
        if (!rowData.update_payment_link) return;
        try {
            await navigator.clipboard.writeText(rowData.update_payment_link);
            toast.success('Update payment link copied to clipboard');
        } catch {
            toast.error('Failed to copy update payment link');
        }
    };

    return (
        <div className='table-row'>
            <div className='memberName'>
                {rowData.member_id ? (
                    <span className='member-link' onClick={handleViewMember}>
                        {displayName}
                    </span>
                ) : (
                    <span>{displayName}</span>
                )}
                {displayEmail && <span className='member-email'>{displayEmail}</span>}
            </div>
            <div className='failureReason' title={rowData.failure_message || undefined}>
                {rowData.failure_reason}
            </div>
            <div className='amountDue'>{formatAmount(rowData.amount_due, rowData.currency)}</div>
            <div className='failedAt'>{formatUKDate(rowData.failed_at, 'dd-MM-yyyy')}</div>
            <div className='subscriptionStatus'>{formatStatus(rowData.subscription_status)}</div>
            <div className='actions'>
                {rowData.update_payment_link && (
                    <Button
                        text='Copy link'
                        onClick={() => void handleCopyLink()}
                        isSecondary={true}
                        height='32px'
                        borderRadius='10px'
                        className='copy-link-button'
                    />
                )}
                {rowData.member_id && (
                    <EyeIcon className='view-icon' onClick={handleViewMember} />
                )}
            </div>
        </div>
    );
};

export default FailedPaymentRow;
